<template>
  <!-- Pass timer component - displays information and control buttons based on pass status -->
  <div
    class="active-pass-timer bg-white rounded-30 d-flex align-items-center justify-content-center w-100"
    :class="{ [timerStatusClass]: true, shadow: !isInQueue }"
  >
    <!-- Information display for queue -->
    <template v-if="isInQueue">
      <!-- Queue position information -->
      <div class="queue-info-container">
        <div class="queue-divider" />

        <div class="queue-position-container">
          <span class="queue-label">{{ $t("common.you_are") }}</span>
          <span class="queue-position-value">
            #{{ activePassQueuePosition }}
          </span>
        </div>
      </div>
    </template>

    <!-- Information display when not in queue -->
    <template v-else>
      <!-- Waiting hourglass icon -->
      <span
        v-if="isWaiting && !isValidAutoPass"
        class="flaticon-hour-glass-2-optimized"
        aria-hidden="true"
      >
        <span class="path1" />
        <span class="path2" />
        <span class="path3" />
        <span class="path4" />
        <span class="path5" />
        <span class="path6" />
        <span class="path7" />
        <span class="path8" />
        <span class="path9" />
        <span class="path10" />
      </span>

      <!-- Start button for auto pass -->
      <div
        v-if="showStartButton"
        class="pt-3 text-green d-flex flex-column align-items-center"
      >
        <button
          type="button"
          class="bg-transparent border-0 p-0 text-green d-flex flex-column align-items-center"
          :aria-label="$t('common.start')"
          data-test-id="student-active-pass-auto-start"
          @click="$emit('auto-approve')"
        >
          <i class="flaticon-play-circle" aria-hidden="true" />
          <span class="font-sm">{{ $t("common.start") }}</span>
        </button>
      </div>

      <!-- Check icon for approved passes -->
      <button
        v-if="showCheckIcon"
        type="button"
        class="fi flaticon-check me-3 bg-transparent border-0 p-0"
        :aria-label="$t('common.check_in')"
        data-test-id="student-active-pass-check-action"
        @click="handleCheckAction"
      />

      <!-- Stop button for active passes -->
      <div
        v-if="showStopButton"
        class="pt-3 d-flex flex-column align-items-center text-red"
      >
        <button
          type="button"
          class="bg-transparent border-0 p-0 d-flex flex-column align-items-center text-red"
          :aria-label="$t('common.stop')"
          data-test-id="student-active-pass-auto-stop"
          @click="$emit('auto-pause')"
        >
          <i class="flaticon-stop-circle" aria-hidden="true" />
          <span class="font-sm">{{ $t("common.stop") }}</span>
        </button>
      </div>

      <!-- Finish line icon for expired passes -->
      <span
        v-if="isExpired"
        class="flaticon-finish-line-optimized me-3"
        aria-hidden="true"
      >
        <span class="path1" />
        <span class="path2" />
        <span class="path3" />
        <span class="path4" />
        <span class="path5" />
        <span class="path6" />
        <span class="path7" />
        <span class="path8" />
        <span class="path9" />
        <span class="path10" />
      </span>

      <!-- Timer display: avoid role="timer" so AT does not treat HH:MM:SS as clock/midnight. -->
      <div
        id="active-pass-timer-time-in-pass"
        class="text-center"
        :aria-label="timerAccessibleLabel"
      >
        {{ displayTime }}
      </div>

      <!-- Check-in button for auto check-in -->
      <button
        v-if="showCheckInButton"
        type="button"
        class="auto-checkin text-green w-100 font-sm text-center bg-transparent border-0"
        data-test-id="student-active-pass-check-in-action"
        @click="$emit('show-check-in')"
      >
        {{ $t("common.check_in") }}
      </button>

      <!-- PIN attempt warning -->
      <div
        v-if="pinAttemptsCount >= 3"
        class="mt-2 text-danger comments-section-warning"
        role="status"
        aria-live="polite"
      >
        {{
          $t("common.this_student_is_flagged_for_3_unsuccessful_pin_attempts")
        }}
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: "PassTimer"
}
</script>

<script setup>
import { computed } from "vue"
import { useI18n } from "vue-i18n"

const { t } = useI18n()

const props = defineProps({
  activePass: {
    type: Object,
    default: () => ({})
  },
  timeInPass: {
    type: String,
    default: ""
  },
  currentStatus: {
    type: String,
    default: ""
  },
  isPassLongRunning: {
    type: Boolean,
    default: false
  },
  pinAttemptsCount: {
    type: Number,
    default: 0
  },
  isActiveAutoCheckInModule: {
    type: Boolean,
    default: false
  }
})

// Define emits
const emit = defineEmits(["auto-approve", "auto-pause", "show-check-in"])

// Computed status flags
const isInQueue = computed(() => props.currentStatus === "queue")
const isWaiting = computed(() => props.currentStatus === "waiting")
const isApproved = computed(() => props.currentStatus === "approved")
const isExpired = computed(() => props.currentStatus === "expired")

// Computed classes based on status
const timerStatusClass = computed(() => {
  if (props.isPassLongRunning) {
    return "text-black"
  }
  if (isWaiting.value) {
    return "text-red"
  }
  if (isApproved.value) {
    return "text-green"
  }
  if (isExpired.value) {
    return "text-red"
  }
  if (isInQueue.value) {
    return "text-black"
  }
  return ""
})

// Get total time from pass
const totalTime = computed(() => {
  return props.activePass?.parent
    ? props.activePass?.parent?.total_time
    : props.activePass?.total_time
})

// Check if auto pass is valid
const isValidAutoPass = computed(() => {
  return (
    props.activePass?.flags &&
    props.activePass?.flags?.auto_pass &&
    props.activePass?.flags?.auto_pass?.is_valid
  )
})

// Show start button logic
const showStartButton = computed(() => {
  return (
    isWaiting.value &&
    isValidAutoPass.value &&
    (props.activePass?.flags?.auto_pass?.mode === "start_and_stop" ||
      props.activePass?.flags?.auto_pass?.mode === "start_only")
  )
})

// Show stop button logic
const showStopButton = computed(() => {
  if (isApproved.value) {
    if (
      props.activePass?.parent?.flags?.auto_pass?.is_valid &&
      (props.activePass?.parent?.flags?.auto_pass?.mode === "start_and_stop" ||
        props.activePass?.parent?.flags?.auto_pass?.mode === "stop_only")
    ) {
      return true
    }

    return (
      !props.activePass?.parent &&
      props.activePass?.flags &&
      props.activePass?.flags?.auto_pass &&
      props.activePass?.flags?.auto_pass?.is_valid &&
      (props.activePass?.flags?.auto_pass?.mode === "start_and_stop" ||
        props.activePass?.flags?.auto_pass?.mode === "stop_only")
    )
  }
  return false
})

// Show check icon logic
const showCheckIcon = computed(() => {
  return (
    isApproved.value &&
    !props.activePass?.parent &&
    props.activePass?.flags &&
    props.activePass?.flags?.auto_pass &&
    !props.activePass?.flags?.auto_pass?.is_valid
  )
})

// Show check-in button logic
const showCheckInButton = computed(() => {
  return (
    isApproved.value &&
    !props.activePass?.parent &&
    props.activePass?.flags &&
    props.activePass?.flags?.auto_check_in &&
    props.activePass?.flags?.auto_check_in?.is_valid === true &&
    props.isActiveAutoCheckInModule
  )
})

// Show queue position
const activePassQueuePosition = computed(() => {
  return props?.activePass?.queue_number || 0
})

// Handle check icon click
const handleCheckAction = () => {
  if (showCheckInButton.value) {
    emit("show-check-in")
  }
}

const displayTime = computed(() => {
  if (isExpired.value) {
    return totalTime.value ?? ""
  }
  return props.timeInPass || ""
})

const timerAccessibleLabel = computed(() => {
  const timeText = displayTime.value
  if (!timeText) {
    return ""
  }
  if (isExpired.value) {
    return t("common.a11y_total_pass_time_value", { time: timeText })
  }
  return t("common.a11y_time_in_pass_value", { time: timeText })
})
</script>
